'use client'

import React, { useEffect, useState, useCallback } from "react";
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { refetchAfterSave } from '@/lib/refetchAfterSave';
import { Checkbox } from "../ui/checkbox";
import { useCreateLeanDetailsMutation, useGetPostStackLeanDetailsQuery, useGetPostStackLeanUniqueWeeksQuery } from '@/redux/slices/preship/preship-api-slice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ChevronsLeft, ChevronsRight, ChevronDown, Loader2, Info } from "lucide-react";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import { Tabs, TabsList } from "@/components/ui/tabs";
import { setPalletLeanWeeks } from "@/redux/slices/pallet/palletSlice";
import { Button } from "../ui/button";
import { useUpdateProjectTargetMutation } from "@/redux/slices/define/defineApiSlice";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { endStatus } from "@/lib/utils";
import LeanConsolidatedView from "../common-components/LeanConsolidatedView";
import { setTarget } from "@/redux/slices/define/defineSlice";
import { StackLeanService } from "@/services/StackLeanService";
import { Skeleton } from "@/components/ui/skeleton";
import EditBanner from "../common-components/EditBanner";

interface LeanDetail {
  id?: number;
  palletLpnNumberId?: number;
  variant: string;
  weekType: string | null;
  sideABottom: string;
  sideATop: string;
  sideALean?: string;
  sideBBottom: string;
  sideBTop: string;
  sideBLean?: string;
  sideCBottom: string;
  sideCTop: string;
  sideCLean?: string;
  sideDBottom: string;
  sideDTop: string;
  sideDLean?: string;
}

interface PalletLpnNumber {
  id?: number;
  lpnNo: string;
  endStatusPallet: string;
  leanDetails: LeanDetail[];
}

interface FormValues {
  palletLpnNumbers: PalletLpnNumber[];
}

interface StackLeanProps {
  variant: string;
  saveCurrentForm?: any,
  hasUnsavedChanges?: boolean,
  onFormChange?: (hasChanges: boolean) => void;
  setHasUnsavedChanges?: any
  isEditing?: boolean
  isOffline?: boolean
  setIsLoading?: any
  palletId?: number
  tabName?: string
  stage?: string
}

const StackLean: React.FC<StackLeanProps> = ({ variant, setIsLoading, saveCurrentForm, onFormChange, palletId, hasUnsavedChanges, setHasUnsavedChanges, tabName, stage, isOffline }) => {
  const { activePallet, palletLeanWeeks } = useSelector((state: RootState) => state.pallets);
  const [activeWeek, setActiveWeek] = useState<string | null>(null);
  const { data: leanData, isFetching, error, refetch, isLoading } = useGetPostStackLeanDetailsQuery(
    { palletId: activePallet?.id, variant, weekType: activeWeek },
    {
      skip: !activePallet?.id || !activeWeek,
      refetchOnMountOrArgChange: true,
    }
  );
  const [isEditing, setIsEditing] = useState(false);
  const { data: uniqueWeeks, refetch: refetchWeeks, isFetching: uniqueWeeksFetching } = useGetPostStackLeanUniqueWeeksQuery(
    { palletId: activePallet?.id, variant },
    { skip: !activePallet?.id, refetchOnMountOrArgChange: true },
  );
  const [week, setWeek] = useState<string[]>([]);
  const { target, projectId } = useSelector((state: RootState) => state.define);
  const [offlineStackLeanData, setOfflineStackLeanData] = useState<any>(null);
  const [offlineUniqueWeeks, setOfflineUniqueWeeks] = useState<any>(null);
  const [createLeanDetails] = useCreateLeanDetailsMutation();
  const [showPalletNo, setShowPalletNo] = useState(true);
  const [showLpnNo, setShowLpnNo] = useState(true);
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([]);
  const [showEndStatus, setShowEndStatus] = useState(true);
  const dispatch = useDispatch();
  const [isDataReady, setIsDataReady] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const [isConsolidatedViewOpen, setIsConsolidatedViewOpen] = useState(false);
  const [updateProjectTarget, { isLoading: updateProjectLoading }] = useUpdateProjectTargetMutation();
  const [newTarget, setNewTarget] = useState<number>(2);
  const [isAdvancedControlsOpen, setIsAdvancedControlsOpen] = useState(false);
  const initializePalletLpnNumbers = useCallback((numberOfPallets: number): PalletLpnNumber[] => {
    return Array.from({ length: numberOfPallets }, (): PalletLpnNumber => ({
      lpnNo: '',
      endStatusPallet: '',
      leanDetails: [{
        variant,
        weekType: activeWeek || '',
        sideABottom: '',
        sideATop: '',
        sideBBottom: '',
        sideBTop: '',
        sideCBottom: '',
        sideCTop: '',
        sideDBottom: '',
        sideDTop: '',
      }],
    }));
  }, [activeWeek, variant]);

  const saveLeanDetails = async (values: FormValues): Promise<boolean> => {
    try {
      const activePalletId = activePallet?.id;
      if (!activePalletId) {
        throw new Error('An active pallet is required to save lean details');
      }

      if (!isOffline) {
        await createLeanDetails({
          palletId: activePalletId,
          palletLpnNumbers: values.palletLpnNumbers.map((lpn) => ({
            ...lpn,
            leanDetails: [lpn.leanDetails[0]],
          })),
        }).unwrap();
      } else {
        await StackLeanService.createStackLeanDetails({
          palletId: Number(activePalletId),
          palletLpnNumbers: values.palletLpnNumbers,
        });
      }
    } catch (error) {
      showErrorToast("Failed to save lean details");
      console.error('Failed to save lean details:', error);
      return false;
    }

    showSuccessToast("Successfully saved!");
    if (!isOffline) {
      // The save may finish after this tab unmounted; refreshing must not report it as failed.
      refetchAfterSave(refetchWeeks);
      refetchAfterSave(refetch);
    }
    return true;
  };

  const markValuesAsSaved = (values: FormValues) => {
    const savedValues = JSON.parse(JSON.stringify(values)) as FormValues;
    setInitialValues(savedValues);
    onFormChange?.(false);
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      palletLpnNumbers: [],
    },
    // enableReinitialize:true,
    onSubmit: async (values) => {
      if (await saveLeanDetails(values)) {
        markValuesAsSaved(values);
      }
    },
  });

  const handleWeekSelection = async (nextWeek: string) => {
    if (nextWeek === activeWeek) return;

    const hasLocalChanges = initialValues !== null
      && JSON.stringify(formik.values) !== JSON.stringify(initialValues);

    if (hasLocalChanges) {
      const valuesToSave = JSON.parse(JSON.stringify(formik.values)) as FormValues;
      const saved = await saveLeanDetails(valuesToSave);
      if (!saved) return;
      markValuesAsSaved(valuesToSave);
    }

    setActiveWeek(nextWeek);
  };
  useEffect(() => {
    setIsLoading(formik.isSubmitting)
  }, [formik.isSubmitting, setIsLoading])
  useEffect(() => {
    if (isOffline && activeWeek) {
      const getData = async () => {
        const res = await StackLeanService.getStackLeanDetails(Number(activePallet?.id), variant, activeWeek)
        setOfflineStackLeanData(res)
      }
      getData()
    }
  }, [isOffline, activeWeek])

  useEffect(() => {
    if (!isOffline) {
      // The weeks query is skipped without a pallet; refetching a skipped query throws.
      if (activePallet?.id) {
        refetchWeeks();
      }
    } else {
      const getData = async () => {
        const res = await StackLeanService.getStackLeanUniqueWeeks(Number(activePallet?.id), variant)
        setOfflineUniqueWeeks(res)
      }
      getData()
    }
  }, [activePallet, variant]);

  useEffect(() => {
    if (!isOffline) {
      if (uniqueWeeks?.weeks) {
        setAvailableWeeks(uniqueWeeks.weeks.includes('Stack Week-4 (unstacking+30M)') ? uniqueWeeks.weeks : [...uniqueWeeks.weeks, 'Stack Week-4 (unstacking+30M)']);
        if (uniqueWeeks.weeks.length > 0 && !activeWeek) {
          setActiveWeek(uniqueWeeks.weeks[0]);
        }
      }
    } else {
      if (offlineUniqueWeeks?.weeks) {
        setAvailableWeeks([...offlineUniqueWeeks.weeks, 'Stack Week-4 (unstacking+30M)'])
        if (offlineUniqueWeeks.weeks.length > 0 && !activeWeek) {
          setActiveWeek(offlineUniqueWeeks.weeks[0])
        }
      }
    }
  }, [uniqueWeeks, isOffline, offlineUniqueWeeks]);

  const handleCheckedChange = (isChecked: boolean, week: string) => {
    if (isChecked) {
      setAvailableWeeks([...availableWeeks, week]);
    } else {
      setAvailableWeeks(availableWeeks.filter((w: string) => w !== week));
    }
  };
  useEffect(() => {
    if (availableWeeks.length === 1) {
      setActiveWeek(availableWeeks[0])
    }
  }, [availableWeeks.length])
  useEffect(() => {
    if (activePallet && !isLoading && !isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (error && 'status' in error && error.status === 404) {
        initialPalletLpnNumbers = initializePalletLpnNumbers(Number(activePallet.numberOfPallets));
      } else if (leanData && leanData.palletLpnNumbers && leanData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = leanData.palletLpnNumbers.map((lpn: PalletLpnNumber) => ({
          ...lpn,
          endStatusPallet: lpn.endStatusPallet || '',
          leanDetails: lpn.leanDetails.length > 0 ? lpn.leanDetails : [{
            variant,
            weekType: activeWeek || "",
            sideABottom: '',
            sideATop: '',
            sideBBottom: '',
            sideBTop: '',
            sideCBottom: '',
            sideCTop: '',
            sideDBottom: '',
            sideDTop: '',
          }]
        }));
      }

      const currentNumberOfPallets = initialPalletLpnNumbers.length;
      const targetNumberOfPallets = Number(activePallet.numberOfPallets);

      if (currentNumberOfPallets < targetNumberOfPallets) {
        initialPalletLpnNumbers.push(...initializePalletLpnNumbers(targetNumberOfPallets - currentNumberOfPallets));
      } else if (currentNumberOfPallets > targetNumberOfPallets) {
        initialPalletLpnNumbers = initialPalletLpnNumbers.slice(0, targetNumberOfPallets);
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [leanData, activePallet, error, activePallet?.numberOfPallets, activeWeek, variant, uniqueWeeks]);
  useEffect(() => {
    if (activePallet && isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (offlineStackLeanData && offlineStackLeanData.palletLpnNumbers && offlineStackLeanData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = offlineStackLeanData.palletLpnNumbers.map((lpn: PalletLpnNumber) => ({
          ...lpn,
          endStatusPallet: lpn.endStatusPallet || '',
          leanDetails: lpn.leanDetails.length > 0 ? lpn.leanDetails : [{
            variant,
            weekType: activeWeek || "",
            sideABottom: '',
            sideATop: '',
            sideBBottom: '',
            sideBTop: '',
            sideCBottom: '',
            sideCTop: '',
            sideDBottom: '',
            sideDTop: '',
          }]
        }));
      }

      const currentNumberOfPallets = initialPalletLpnNumbers.length;
      const targetNumberOfPallets = Number(activePallet.numberOfPallets);

      if (currentNumberOfPallets < targetNumberOfPallets) {
        initialPalletLpnNumbers.push(...initializePalletLpnNumbers(targetNumberOfPallets - currentNumberOfPallets));
      } else if (currentNumberOfPallets > targetNumberOfPallets) {
        initialPalletLpnNumbers = initialPalletLpnNumbers.slice(0, targetNumberOfPallets);
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [isOffline, offlineStackLeanData, activePallet, activeWeek]);
  useEffect(() => {
    // The lean query is skipped until a week is selected; refetching a skipped query throws.
    if (activePallet?.id && activeWeek) {
      refetch();
    }
  }, [activePallet?.id, activePallet?.numberOfPallets, activeWeek, refetch]);
  const endStatusOptions = [{
    label: "Pre-Ship",
    value: "Pre Ship"
  }, {
    label: "Post-Ship",
    value: "Post Ship"
  },
  { label: "Stack start(stacked+30m)", value: "Stack start(stacked+30m)" },
  { label: "Stack Week-1", value: "Stack Week-1" },
  { label: "Stack Week-2", value: "Stack Week-2" },
  { label: "Stack Week-3", value: "Stack Week-3" },
  { label: "Stack Week-4 (unstacking+30M)", value: "Stack Week-4 (unstacking+30M)" }
  ]
  const stackLeanWeeks = [
  { label: "Stack start(stacked+30m)", value: "Stack start(stacked+30m)" },
  { label: "Stack Week-1", value: "Stack Week-1" },
  { label: "Stack Week-2", value: "Stack Week-2" },
  { label: "Stack Week-3", value: "Stack Week-3" },
  { label: "Stack Week-4 (unstacking+30M)", value: "Stack Week-4 (unstacking+30M)" }
  ]
  useEffect(() => {
    const index = endStatusOptions.findIndex(item => item.value === activeWeek);
    const selectedItems = endStatusOptions.slice(index);
    setWeek(selectedItems.map(item => item.value));
  }, [activeWeek]);

  const updateTarget = async () => {
    try {
      const res = await updateProjectTarget({ target: Number(newTarget), projectId }).unwrap();
      dispatch(setTarget(Number(newTarget)));
      showSuccessToast("Successfully updated!");
    } catch (e) {
      console.log(e);
      showErrorToast("Failed to update the target");
    }
  };

  const calculateLean = useCallback((top: string, bottom: string) => {
    const topValue = parseFloat(top);
    const bottomValue = parseFloat(bottom);
    if (!isNaN(topValue) && !isNaN(bottomValue)) {
      return (bottomValue - topValue).toFixed(2);
    }
    return '';
  }, []);

  const getLeanCellColor = useCallback((leanValue: string, target: number) => {
    const numericLean = parseFloat(leanValue);
    if (!isNaN(numericLean)) {
      if (numericLean >= -target && numericLean <= target) {
        return 'bg-states-success text-white';
      } else {
        return 'bg-bgRed text-white';
      }
    }
    return '';
  }, []);

  // Add roundToNearestQuarter function
  const roundToNearestQuarter = (value: string): string => {
    if (!value || isNaN(Number(value))) return value;
    const num = parseFloat(value);
    const rounded = Math.round(num * 4) / 4;
    return rounded.toFixed(2);
  };

  // Add arrow key navigation handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIndex: number, colIndex: number) => {
    let targetRow = rowIndex;
    let targetCol = colIndex;
    switch (e.key) {
      case 'ArrowUp': targetRow = Math.max(0, rowIndex - 1); break;
      case 'Enter': // Adding Enter key to behave like Down arrow - Excel behavior
      case 'ArrowDown': targetRow = Math.min(formik.values.palletLpnNumbers.length - 1, rowIndex + 1); break;
      case 'ArrowLeft': targetCol = Math.max(2, colIndex - 1); break;
      case 'ArrowRight': targetCol = Math.min(14, colIndex + 1); break;
      default: return;
    }
    e.preventDefault();
    const nextEl = document.querySelector(`[data-row="${targetRow}"][data-col="${targetCol}"]`) as HTMLElement | null;
    nextEl?.focus();
  }, [formik.values.palletLpnNumbers.length]);

  useEffect(() => {
    if (onFormChange && initialValues) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      onFormChange(hasChanges);
    }
  }, [formik.values, onFormChange, initialValues]);
  console.log('uniqueWeeks', uniqueWeeks)
  useEffect(() => {
    return () => {
      if (isEditing) {
        setIsEditing(false)
      }
    }
  }, [isEditing])
  // if (isFetching || uniqueWeeksFetching) {
  //   return (
  //     <div className="space-y-6 w-full animate-pulse">
  //       {/* Header skeleton */}
  //       <div className="flex justify-between items-center">
  //         <Skeleton className="h-8 w-48" />
  //         <Skeleton className="h-10 w-32" />
  //       </div>

  //       {/* Tabs skeleton */}
  //       <div className="flex gap-3">
  //         {[1, 2, 3].map((i) => (
  //           <Skeleton key={i} className="h-10 w-32 rounded-md" />
  //         ))}
  //         <Skeleton className="h-10 w-64 rounded-md" />
  //       </div>

  //       {/* Table header skeleton */}
  //       <div className="border rounded-md overflow-hidden">
  //         <div className="border-b bg-gray-50">
  //           <div className="grid grid-cols-5 p-3">
  //             <Skeleton className="h-6 w-full mx-2" />
  //             {Array(4).fill(0).map((_, i) => (
  //               <Skeleton key={i} className="h-6 w-full mx-2" />
  //             ))}
  //           </div>
  //         </div>

  //         {/* Table rows skeleton */}
  //         <div className="p-2">
  //           {[1, 2, 3, 4, 5].map((i) => (
  //             <div key={i} className="grid grid-cols-5 gap-2 p-2 border-b last:border-0">
  //               <Skeleton className="h-10 w-full" />
  //               {Array(4).fill(0).map((_, j) => (
  //                 <Skeleton key={j} className="h-10 w-full" />
  //               ))}
  //             </div>
  //           ))}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  console.log(leanData, 'leanData', formik.values, hasUnsavedChanges)
  return (
    <div className=" space-y-2">
      {!isOffline && <EditBanner

        stage={stage}
        palletId={palletId}
        component={tabName}
        onEditStateChange={setIsEditing}
      />}
      {
        (isFetching || uniqueWeeksFetching) ?
          <div className="space-y-6 w-full animate-pulse">
            {/* Header skeleton */}
            <div className="flex justify-between items-center">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-10 w-32" />
            </div>

            {/* Tabs skeleton */}
            <div className="flex gap-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-32 rounded-md" />
              ))}
              <Skeleton className="h-10 w-64 rounded-md" />
            </div>

            {/* Table header skeleton */}
            <div className="border rounded-md overflow-hidden">
              <div className="border-b bg-gray-50">
                <div className="grid grid-cols-5 p-3">
                  <Skeleton className="h-6 w-full mx-2" />
                  {Array(4).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-6 w-full mx-2" />
                  ))}
                </div>
              </div>

              {/* Table rows skeleton */}
              <div className="p-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="grid grid-cols-5 gap-2 p-2 border-b last:border-0">
                    <Skeleton className="h-10 w-full" />
                    {Array(4).fill(0).map((_, j) => (
                      <Skeleton key={j} className="h-10 w-full" />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          :
          <div className={`flex flex-col gap-2`}>
            <Tabs defaultValue={activeWeek || ""} className="">
              <TabsList className="flex justify-start gap-3 bg-white ">
                {availableWeeks.map((week: string, i: number) => (
                  <div
                    onClick={() => void handleWeekSelection(week)}
                    key={i}
                    className={`${activeWeek === week ? "border border-blue  bg-light-blue font-semibold text-blue" : " bg-states-disabled font-semibold"} cursor-pointer text-sm rounded-md p-2 h-10 flex items-center justify-center`}
                  >
                    {week}
                  </div>
                ))}
                <DropdownMenu>
                  <DropdownMenuTrigger className="w-[300px] rounded-xl h-10 flex items-center  border border-blue text-blue font-semibold data-[state=open]:border-blue data-[state=open]:rounded-bl-none data-[state=open]:rounded-br-none data-[state=open]:border-b data-[state=open]:border-b-border-style-1 py-3 text-sm text-left px-3 relative">
                    Select Weeks
                    <ChevronDown size={20} className="text-blue absolute right-2 top-3" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[300px] data-[state=open]:border-blue border data-[state=open]:rounded-tl-none data-[state=open]:rounded-tr-none data-[state=open]:border-t-0">
                    {stackLeanWeeks.map((item, _i: number) => (
                      <div key={_i} className="flex items-center first:border-t-0 border-t border-border-style-1 justify-start gap-2 p-2 hover:bg-light-blue">
                        <Checkbox
                          checked={availableWeeks.includes(item.value)}
                          onCheckedChange={(checked: boolean) => handleCheckedChange(checked, item.value)}
                          className="rounded-full"
                          id={item.value}
                        />
                        <label htmlFor={item.value} className="text-sm font-medium py-1 leading-none peer-disabled:cursor-not-allowed cursor-pointer peer-disabled:opacity-70">
                          {item.label}
                        </label>
                      </div>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TabsList>
            </Tabs>
            {(activePallet && activeWeek) && (
              <form id={`stack-lean-table-${activePallet?.id}`} onSubmit={formik.handleSubmit}>
                <Table className={`${(isEditing) || (isOffline) ? " " : "pointer-events-none"} border-collapse`}>
                  <TableHeader>

                    <TableRow>
                      <TableHead className="border border-gray-300 text-blue p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left" colSpan={3}>
                        Pallet Name: {activePallet?.name || 'N/A'} - Mode {activePallet?.shippingModes?.map((sp) => sp.mode).join(', ') || 'N/A'}
                      </TableHead>
                      <TableHead className="border border-gray-300 text-blue p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left" colSpan={12}>
                        <div className='flex items-center gap-2 justify-between'>
                          <div>
                            <span className="text-xs flex items-center gap-2"><Info className="w-4 h-4" /> Note: Data will be rounded to the nearest 0.25 inch</span>


                            {isAdvancedControlsOpen && (
                              <div className="fixed inset-0 !pointer-events-auto bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white p-6 rounded-lg w-[500px]">
                                  <h2 className="text-xl font-bold mb-4">Advanced Controls</h2>
                                  <div className="space-y-4">
                                    <div className="flex flex-col gap-2">
                                      <label className=" text-base">Max Pallet Lean (Not greater than)</label>
                                      <div className="flex items-center gap-4">
                                        <input
                                          onWheel={(e: React.WheelEvent<HTMLInputElement>) => e.target.blur()}
                                          onChange={(e) => setNewTarget(e.target.value)}
                                          value={newTarget}
                                          type='number'
                                          className="border-2 border-blue px-2 py-1 text-base"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex justify-end gap-2">

                                      <Button
                                        type='button'
                                        onClick={() => setIsAdvancedControlsOpen(false)}
                                        className="bg-gray-200 text-gray-800 hover:bg-bgRed hover:text-white"
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        type='button'
                                        onClick={() => {
                                          updateTarget();
                                          setIsAdvancedControlsOpen(false);
                                        }}
                                        disabled={updateProjectLoading}
                                        className="bg-blue text-white flex items-center gap-2"
                                      >
                                        {updateProjectLoading && <Loader2 className='animate-spin' />}
                                        Update
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                     {!isOffline &&     <div className="flex-center gap-4 !pointer-events-auto">
                            <Button
                              onClick={() => setIsAdvancedControlsOpen(true)}
                              type='button'
                              className="bg-white text-md hover:bg-blue hover:text-white font-semibold text-blue border-2 border-blue"
                            >
                              Advance Controls
                            </Button>
                            <div className="flex-center gap-4">
                              {/* <Button type='button' className="bg-white text-md font-semibold text-blue border-2 border-blue">
                        Clear All
                      </Button> */}
                              <Button
                                onClick={() => setIsConsolidatedViewOpen(true)}
                                type='button' className="bg-white hover:bg-blue hover:text-white text-md font-semibold text-blue border-2 border-blue">
                                Consolidated View
                              </Button>
                            </div>
                          </div>}
                        </div>
                      </TableHead>
                    </TableRow>
                    <TableRow>
                      <TableHead className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center" colSpan={3}>
                        {variant === 'preShip' ? 'Pre-Ship' : variant === 'postShip' ? 'Post-Ship' : "Post Stack"} Pallet Lean
                      </TableHead>
                      {['Side A', 'Side B', 'Side C', 'Side D'].map((side) => (
                        <TableHead key={side} className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center" colSpan={3}>
                          {side}
                        </TableHead>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableHead className={cn(
                        "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                        !showPalletNo && "w-[20px] bg-blue"
                      )}>
                        {showPalletNo && "Pallet No"}
                        <span
                          onClick={() => setShowPalletNo(!showPalletNo)}
                          className={cn(

                            "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                            showPalletNo && "right-2 top-4 absolute"
                          )}
                        >
                          {showPalletNo ? (
                            <ChevronsLeft size={20} className="text-blue" />
                          ) : (
                            <ChevronsRight size={20} className="text-white" />
                          )}
                        </span>
                      </TableHead>
                      <TableHead className={cn(
                        "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                        !showLpnNo && "w-[20px] bg-blue"
                      )}>
                        {showLpnNo && "Pallet Identifier"}
                        <span
                          onClick={() => setShowLpnNo(!showLpnNo)}
                          className={cn(
                            "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                            showLpnNo && "right-2 top-4 absolute"
                          )}
                        >
                          {showLpnNo ? (
                            <ChevronsLeft size={20} className="text-blue" />
                          ) : (
                            <ChevronsRight size={20} className="text-white" />
                          )}
                        </span>
                      </TableHead>
                      <TableHead className={cn(
                        "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                        !showEndStatus && "w-[20px] bg-blue"
                      )}>
                        {showEndStatus && "End Status"}
                        <span
                          onClick={() => setShowEndStatus(!showEndStatus)}
                          className={cn(
                            "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                            showEndStatus && "right-2 top-4 absolute"
                          )}
                        >
                          {showEndStatus ? (
                            <ChevronsLeft size={20} className="text-blue" />
                          ) : (
                            <ChevronsRight size={20} className="text-white" />
                          )}
                        </span>
                      </TableHead>
                      {['Side A', 'Side B', 'Side C', 'Side D'].flatMap((side) =>
                        ['Top', 'Bottom', 'Lean'].map((header) => (
                          <TableHead
                            key={`${side}-${header}`}
                            className="border border-gray-300 text-black p-0 text-left px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue"
                          >
                            {header}
                          </TableHead>
                        ))
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formik.values.palletLpnNumbers.map((palletLpn: PalletLpnNumber, index: number) => {
                      const isRowEditable = week.includes(palletLpn.endStatusPallet);
                      return (
                        <TableRow key={index} className={cn("w-2/3 bg-white")}>
                          <TableCell className={cn("border border-gray-300 p-0 bg-states-disabled", !showPalletNo && "bg-blue")}>
                            {showPalletNo && (
                              <Input
                                className="w-full border-none "
                                value={index + 1}
                                readOnly
                              />
                            )}
                          </TableCell>
                          <TableCell className={cn("border bg-states-disabled border-gray-300 p-0", !showLpnNo && "bg-blue")}>
                            {showLpnNo && (
                              <Input
                                className="w-full  border-none "
                                value={palletLpn.lpnNo}
                                readOnly
                              />
                            )}
                          </TableCell>
                          <TableCell className={cn("border border-gray-300 p-0", !showEndStatus && "bg-blue")}>
                            {showEndStatus && (
                              <Select
                                value={palletLpn.endStatusPallet || undefined}
                                onValueChange={(value) => {
                                  if (value !== '') {
                                    formik.setFieldValue(`palletLpnNumbers.${index}.endStatusPallet`, value);
                                  }
                                  console.log(`Updated endStatusPallet for index ${index}:`, value);
                                }}
                              >
                                <SelectTrigger
                                  data-row={index} data-col={2}
                                  onKeyDown={(e) => handleKeyDown(e, index, 2)}
                                  className="w-full border-2 border-gray-300 rounded-none bg-transparent focus:border-blue"
                                >
                                  <SelectValue placeholder="Select End Status" />
                                </SelectTrigger>
                                <SelectContent>
                                  {endStatus.map((status) => (
                                    <SelectItem key={status.value} value={status.value}>
                                      {status.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>
                          {['A', 'B', 'C', 'D'].map((side, sideIndex) => (
                            <React.Fragment key={side}>
                              {['Top', 'Bottom', 'Lean'].map((position, posIndex) => {
                                const fieldName = `side${side}${position}` as keyof LeanDetail;
                                const isLeanField = position === 'Lean';
                                const bottomValue = palletLpn.leanDetails[0][`side${side}Bottom` as keyof LeanDetail] as string;
                                const topValue = palletLpn.leanDetails[0][`side${side}Top` as keyof LeanDetail] as string;
                                const leanValue = calculateLean(topValue, bottomValue);
                                // Calculate column index for navigation
                                const colIndexValue = 3 + sideIndex * 3 + posIndex;

                                return (
                                  <TableCell
                                    key={`${side}${position}`}
                                    className={cn(
                                      "border border-gray-300 p-0",
                                      isLeanField && "bg-states-disabled",
                                      isLeanField && getLeanCellColor(leanValue, Number(target))
                                    )}
                                  >
                                    <Input
                                      data-row={index} data-col={colIndexValue}
                                      onKeyDown={(e) => handleKeyDown(e, index, colIndexValue)}
                                      className={cn("w-full h-9 border-none focus:border-blue", !isRowEditable && "bg-gray-200")}
                                      type={isLeanField ? "text" : "number"}
                                      step="0.25"
                                      placeholder={isLeanField ? "Value" : "Enter"}
                                      value={isLeanField ? leanValue : palletLpn.leanDetails[0][fieldName] as string}
                                      onChange={(e) => {
                                        if (!isLeanField && isRowEditable) {
                                          formik.setFieldValue(`palletLpnNumbers.${index}.leanDetails.0.${fieldName}`, e.target.value);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        if (!isLeanField && isRowEditable && e.target.value) {
                                          const roundedValue = roundToNearestQuarter(e.target.value);
                                          formik.setFieldValue(`palletLpnNumbers.${index}.leanDetails.0.${fieldName}`, roundedValue);
                                        }
                                      }}
                                      readOnly={isLeanField || !isRowEditable}
                                      disabled={!isRowEditable}
                                    />
                                  </TableCell>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <button type="submit" style={{ display: 'none' }}></button>
                {<LeanConsolidatedView
                  isOpen={isConsolidatedViewOpen}
                  onClose={() => setIsConsolidatedViewOpen(false)}
                  variant={variant}
                  activeWeek={Array.isArray(uniqueWeeks?.weeks) ? uniqueWeeks?.weeks[uniqueWeeks?.weeks?.length - 1] : ''}
                />}
              </form>
            )}
          </div>
      }
    </div>
  );
};

export default StackLean;
